import type { TokenCredential } from "@azure/identity";
import type { AgentDefinition } from "./foundry-agent-get.js";
import { LocalCliError } from "./errors.js";

const FOUNDRY_DATA_SCOPE = "https://ai.azure.com/.default";

/**
 * POST a new Foundry agent version via raw REST. Shared by a2a-enable,
 * brain-link (and future callers) — the request shape must stay
 * byte-identical to what those callers' tests already assert on.
 *
 * Every version POST is followed by ensureAgentEndpointEntraIsolation's
 * healing PATCH (below) before the new version id is returned.
 */
export async function createAgentVersion(
  args: { credential: TokenCredential; projectEndpoint: string; agentName: string; definition: AgentDefinition; metadata: Record<string, string>; extraHeaders?: Record<string, string> },
  fetchImpl: typeof fetch = fetch,
): Promise<string> {
  const token = await args.credential.getToken(FOUNDRY_DATA_SCOPE);
  if (!token?.token) throw new LocalCliError({ code: "FOUNDRY_AUTH", message: "Could not acquire Foundry data-plane token" });
  const url = `${args.projectEndpoint}/agents/${args.agentName}/versions?api-version=v1`;
  const res = await fetchImpl(url, {
    method: "POST",
    headers: { Authorization: `Bearer ${token.token}`, "Content-Type": "application/json", ...(args.extraHeaders ?? {}) },
    body: JSON.stringify({ definition: args.definition, metadata: args.metadata }),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new LocalCliError({ code: "AGENT_CREATE_VERSION_FAILED", message: `POST ${url}: HTTP ${res.status.toString()}\n${text.slice(0, 500)}` });
  }
  // The agent exists on the service once the POST above succeeds — heal the
  // endpoint auth scheme here, before the client-side version-field check
  // below, so the PATCH still runs even if that check throws.
  await ensureAgentEndpointEntraIsolation(
    { credential: args.credential, projectEndpoint: args.projectEndpoint, agentName: args.agentName, extraHeaders: args.extraHeaders },
    fetchImpl,
  );
  const data = (await res.json()) as { version?: string };
  if (!data.version) throw new LocalCliError({ code: "AGENT_CREATE_VERSION_NO_VERSION", message: "createVersion returned no version" });
  return data.version;
}

// Agents created or updated through the bare versions API
// (POST {projectEndpoint}/agents/{name}/versions?api-version=v1) come up with
// an agent_endpoint Entra authorization scheme that lacks `isolation_key_source`.
// The @azure/ai-projects deserializer reads that field unconditionally, so
// every SDK `agents.get` on such an agent throws "Cannot read properties of
// undefined (reading 'kind')". This PATCH backfills the field so the agent
// stays SDK-readable — idempotent, re-asserted after every version post.
export async function ensureAgentEndpointEntraIsolation(
  args: { credential: TokenCredential; projectEndpoint: string; agentName: string; extraHeaders?: Record<string, string> },
  fetchImpl: typeof fetch = fetch,
): Promise<void> {
  const token = await args.credential.getToken(FOUNDRY_DATA_SCOPE);
  if (!token?.token) throw new LocalCliError({ code: "FOUNDRY_AUTH", message: "Could not acquire Foundry data-plane token" });
  const url = `${args.projectEndpoint}/agents/${args.agentName}?api-version=v1`;
  const res = await fetchImpl(url, {
    method: "PATCH",
    headers: { Authorization: `Bearer ${token.token}`, "Content-Type": "application/json", ...(args.extraHeaders ?? {}) },
    body: JSON.stringify({ agent_endpoint: { authorization_schemes: [{ type: "Entra", isolation_key_source: { kind: "Entra" } }] } }),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new LocalCliError({ code: "AGENT_ENDPOINT_AUTH_PATCH_FAILED", message: `PATCH ${url}: HTTP ${res.status.toString()}\n${text.slice(0, 500)}` });
  }
}
